/**
 * Advisor Selection Hooks
 * React Query hooks for advisor selection status and operations
 *
 * T034 - US2: useSelectionStatus hook for post-login redirect logic
 * T045 - US3: useAdvisorSearch hook with debouncing
 * T046 - US3: useSelectAdvisor mutation hook
 * FR-036: Check advisor selection status
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  getSelectionStatus,
  searchAdvisors,
  selectAdvisor,
  skipAdvisorSelection,
} from '../api/partnerSignupApi';

/**
 * Hook to fetch user's advisor selection status
 * Used post-login to determine if user should be redirected to /select-advisor
 *
 * Returns:
 * - onboardingStep: Current step in partner signup onboarding
 * - requiresAdvisorSelection: Boolean indicating if user should see selection page
 * - partnerCompany: Partner company info (if applicable)
 * - hasAdvisor: Whether user already has a selected advisor
 *
 * FR-010, FR-011, FR-012, FR-036
 */
export const useSelectionStatus = () => {
  return useQuery({
    queryKey: ['advisor-selection', 'status'],
    queryFn: getSelectionStatus,
    staleTime: 1000 * 60 * 5, // 5 minutes - status doesn't change often
    retry: 1, // Only retry once on failure
  });
};

/**
 * Hook to search for financial advisors with debouncing
 * Debounces search query by 300ms to avoid excessive API calls
 *
 * Only triggers search when query is 2+ characters
 *
 * @param query - Search query string
 * @param page - Page number (1-indexed)
 * @param limit - Results per page
 *
 * FR-016, FR-017, FR-018, FR-019, FR-020
 */
export const useAdvisorSearch = (
  query: string,
  page: number = 1,
  limit: number = 10,
) => {
  const [debouncedQuery, setDebouncedQuery] = useState(query);

  // Debounce query by 300ms (FR-020)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const queryResult = useQuery({
    queryKey: ['advisor-selection', 'search', debouncedQuery, page, limit],
    queryFn: () => searchAdvisors(debouncedQuery, page, limit),
    enabled: debouncedQuery.trim().length >= 2, // Only search with 2+ chars (FR-017)
    staleTime: 1000 * 60, // 1 minute - search results change infrequently
    retry: 1,
  });

  return {
    ...queryResult,
    debouncedQuery, // Expose debounced query for UI feedback
  };
};

/**
 * Hook to select a financial advisor
 * Creates a client-advisor relationship and updates user's onboarding step
 *
 * On success:
 * - Invalidates selection status query (to refresh user state)
 * - Redirects to the path specified in response.redirectTo
 *
 * FR-021, FR-022, FR-023, FR-024, FR-025, FR-028, FR-029, FR-030, FR-032, FR-033
 */
export const useSelectAdvisor = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: selectAdvisor,
    onSuccess: (data) => {
      // Invalidate status query to refresh user state
      queryClient.invalidateQueries({ queryKey: ['advisor-selection', 'status'] });

      // Redirect to the appropriate destination
      if (data.redirectTo) {
        navigate(data.redirectTo);
      }
    },
  });
};

/**
 * Hook to skip advisor selection
 * Updates user's onboarding step to 'skipped_advisor_selection'
 *
 * User can skip multiple times and will be prompted again on subsequent logins
 *
 * On success:
 * - Invalidates selection status query (to refresh user state)
 * - Redirects to the path specified in response.redirectTo
 *
 * FR-026, FR-027
 */
export const useSkipAdvisorSelection = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: skipAdvisorSelection,
    onSuccess: (data) => {
      // Invalidate status query to refresh user state
      queryClient.invalidateQueries({ queryKey: ['advisor-selection', 'status'] });

      // Redirect to the appropriate destination
      if (data.redirectTo) {
        navigate(data.redirectTo);
      }
    },
  });
};
